import { useMemo, useRef } from 'react'
import type { PropsWithChildren, ReactNode } from 'react'

import { useApolloClient } from '@apollo/client'
import { Box, theme } from '@factorialco/deprecated-design-system'
import { OneDataCollection, useDataCollectionData } from '@factorialco/f0-react/dist/experimental'
import { Calendar } from '@factorialco/f0-react/icons/app'
import {
  CalendarEmployeesDocument,
  EmployeeCollectionFilterEmployeeGroupNamesDocument,
  EmployeeCollectionFilterJobTitlesDocument,
  TeamViewEmployeesDocument,
  type TeamViewEmployeesQuery,
} from 'generated/resources/graphql'
import debounce from 'lodash/debounce'
import uniqBy from 'lodash/uniqBy'
import type moment from 'moment'

import { observer } from 'mobx-react'

import { DateNavigator } from 'modules/core/components/DateNavigator'
import { useUnsafeAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import Exports from 'modules/calendar/components/Exports'
import type { CalendarEmployeeLookup } from 'modules/calendar/lib/calendarEmployeeData'
import type { CalendarRootRenderProps } from 'modules/calendar/lib/RouteContextParams'
import generateFetchData from 'modules/calendar/lib/generateFetchData'
import calendarEvents from 'modules/calendar/store/collections/calendarEvents'
import { useEmployeeFilters } from 'modules/employees/lib/filters'
import { useEmployeesFilter } from 'modules/employees/lib/filters/employees'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import TeamViewTable from './components/TeamViewTable'

export type TeamViewEmployee =
  TeamViewEmployeesQuery['employees']['employeesConnection']['edges'][number]['node']

type Props = CalendarRootRenderProps & {
  lastLeaveUpdatedInMilliseconds: number
}

type TeamViewSource = ReturnType<typeof useTeamViewSource>

const useTeamViewSource = (
  companyId: string,
  hasJobCatalogTreeFeature: boolean,
  date: string,
  canSeeJobTitle: boolean,
  employeeFilters: ReturnType<typeof useEmployeeFilters>['filters'],
  employeeNameFilter: ReturnType<typeof useEmployeesFilter>
) => {
  const client = useApolloClient()

  return useGraphqlDataCollectionSource(
    {
      document: TeamViewEmployeesDocument,
      perPage: 20,
      paginationType: 'infinite-scroll',
      mapVariables: ({ filters, search }) => {
        const teamIds = filters.team?.map((id) => id)
        const locationIds = filters.workplace?.map((id) => id)
        const legalEntityIds = filters.legalEntity?.map((id) => id)
        const jobLevelIds = !hasJobCatalogTreeFeature ? filters.jobRole?.map((id) => id) : undefined
        const jobCatalogTreeNodeIds = hasJobCatalogTreeFeature
          ? filters.jobRole?.map((id) => id.toString())
          : undefined
        const reportingTo = filters.reportingTo?.map((id) => id)
        const timeoffManagerIds = filters.timeoffManager?.map((id) => id)
        const jobTitles = filters.jobTitle?.map((title) => title)
        const employeeGroupIds = filters.employeeGroup?.map((id) => id)
        const employeeIds = filters.employeeName?.map((id) => id)

        return {
          companyId,
          search: search,
          teamIds: teamIds?.length ? teamIds : undefined,
          locationIds: locationIds?.length ? locationIds : undefined,
          legalEntityIds: legalEntityIds?.length ? legalEntityIds : undefined,
          referenceContractLevelIds: jobLevelIds?.length ? jobLevelIds : undefined,

          referenceContractTreeLevelUuids: jobCatalogTreeNodeIds?.length
            ? jobCatalogTreeNodeIds
            : undefined,
          reportingTo: reportingTo?.length ? reportingTo : undefined,
          timeoffManagerIds: timeoffManagerIds?.length ? timeoffManagerIds : undefined,
          onlyMyReportees: filters.myReportees?.includes('all') ? true : undefined,
          onlyDirectReports:
            filters.myReportees?.includes('direct') && !filters.myReportees?.includes('all')
              ? true
              : undefined,
          referenceContractJobTitles: jobTitles?.length ? jobTitles : undefined,
          employeeGroupIds: employeeGroupIds?.length ? employeeGroupIds : undefined,
          employeeIds: employeeIds?.length ? employeeIds : undefined,
          activeOn: date,
          sortOrder: { field: 'firstName', order: 'asc' },
        }
      },
      mapPagination: (data) => {
        const connection = data.employees.employeesConnection
        type ConnectionWithPageInfo = typeof connection & {
          pageInfo?: { endCursor?: string | null; hasNextPage?: boolean }
        }
        const connectionWithPageInfo: ConnectionWithPageInfo = connection
        return {
          total: connection.totalCount ?? 0,
          cursor: connectionWithPageInfo.pageInfo?.endCursor ?? undefined,
          hasNextPage: connectionWithPageInfo.pageInfo?.hasNextPage ?? false,
        }
      },
      mapData: (data) => data.employees.employeesConnection.edges.map((edge) => edge.node),
      search: {
        enabled: true,
      },

      filters: {
        ...employeeFilters,
        ...(canSeeJobTitle
          ? {
              jobTitle: {
                type: 'in' as const,
                label: i18n.t('employees.employees_list.filters.job_title'),
                options: {
                  options: async () => {
                    const { data } = await client.query({
                      query: EmployeeCollectionFilterJobTitlesDocument,
                      variables: {
                        companyId: companyId,
                      },
                      fetchPolicy: 'cache-first',
                    })

                    const jobTitles = data.employees.employees
                      .map((employee) => {
                        return employee.referenceContract?.jobTitle
                          ? {
                              label: employee.referenceContract?.jobTitle,
                              value: employee.referenceContract?.jobTitle,
                            }
                          : undefined
                      })
                      .filter(Boolean)

                    return uniqBy(jobTitles, (item) => item.value.toLowerCase().trim())
                  },
                },
              },
            }
          : {}),
        employeeGroup: {
          type: 'in',
          label: i18n.t('employees.employees_list.filters.employee_group'),
          options: {
            options: async () => {
              const { data } = await client.query({
                query: EmployeeCollectionFilterEmployeeGroupNamesDocument,
                variables: {
                  companyId: companyId,
                },
                fetchPolicy: 'cache-first',
              })

              return data.companies.legalEntitiesConnection.nodes
                .map((legalEntity) => {
                  return legalEntity.employeeGroupsConnection.nodes.map((employeeGroup) => ({
                    label: employeeGroup.name,
                    value: employeeGroup.id,
                  }))
                })
                .flat()
            },
          },
        },
        employeeName: employeeNameFilter,
      },
    },
    [companyId, hasJobCatalogTreeFeature, date, canSeeJobTitle]
  )
}

interface TeamViewVisualizationProps {
  source: TeamViewSource
  date: moment.Moment
  changingData: boolean
  employeeLookup: CalendarEmployeeLookup
  children: ReactNode
}

const TeamViewVisualization = observer(function TeamViewVisualization({
  source,
  date,
  changingData,
  employeeLookup,
  children,
}: TeamViewVisualizationProps) {
  const { data, isLoading, isLoadingMore, loadMore, paginationInfo } = useDataCollectionData(source)

  const employeesWorkingDays = useMemo(() => {
    return data.records.reduce<Record<string, string>>((acc, emp) => {
      acc[String(emp.id)] = emp.contractVersions[0]?.workingWeekDays ?? ''
      return acc
    }, {})
  }, [data.records])

  const hasMore = paginationInfo && 'hasMore' in paginationInfo ? paginationInfo.hasMore : false
  const hasMoreRef = useRef(hasMore)
  const isLoadingMoreRef = useRef(isLoadingMore)
  hasMoreRef.current = hasMore
  isLoadingMoreRef.current = isLoadingMore

  const scrollListener = useMemo(
    () =>
      debounce(() => {
        if (hasMoreRef.current && !isLoadingMoreRef.current) {
          loadMore()
        }
      }, 300),
    [loadMore]
  )

  return (
    <Box padding="s16">
      <div
        style={{
          width: '100%',
          height: 'calc(100vh - 324px)',
          marginBottom: `calc(${theme.dimensions.bodyPadding} * -1)`,
          overflow: 'auto',
          position: 'relative',
          zIndex: '1',
        }}
      >
        <TeamViewTable
          date={date}
          changingData={changingData || (isLoading && data.records.length === 0)}
          employees={data.records}
          employeesWorkingDays={employeesWorkingDays}
          scrollListener={scrollListener}
          paginationInfo={paginationInfo}
          employeeLookup={employeeLookup}
        />
        {children}
      </div>
    </Box>
  )
})

const TeamViewPage = ({
  loading,
  children,
  date,
  range,
  onDateRangeChange,
  canSeeCalendar,
  canSeeJobTitle,
  lastLeaveUpdatedInMilliseconds,
}: PropsWithChildren<Props>) => {
  const { company } = useCurrentSession()
  const { filters: employeeFilters, hasJobCatalogTreeFeature } = useEmployeeFilters()
  const employeeNameFilter = useEmployeesFilter({
    label: i18n.t('employees.employees_list.filters.employee_name'),
  })

  const { loading: changingData } = useUnsafeAsyncData(() => {
    const data = generateFetchData(date, '', false)
    const ms = lastLeaveUpdatedInMilliseconds
    calendarEvents.reset([])

    return Promise.all([
      calendarEvents.fetch({
        data: { ...data, ms },
      }),
    ])
  }, [date, lastLeaveUpdatedInMilliseconds])

  const allEvents = calendarEvents.toArray()
  const employeeIds = useMemo(() => {
    const ids = new Set<string>()
    for (const event of allEvents) {
      const eid = event.metadata('employee_id')
      if (eid) ids.add(eid)
    }
    return Array.from(ids)
  }, [allEvents])

  const { data: employeesData } = useQuery(CalendarEmployeesDocument, {
    variables: { ids: employeeIds },
    skip: employeeIds.length === 0,
  })

  const employeeLookup: CalendarEmployeeLookup = useMemo(() => {
    const map: CalendarEmployeeLookup = new Map()
    if (employeesData) {
      for (const edge of employeesData.employees.employeesConnection.edges) {
        map.set(edge.node.id, edge.node)
      }
    }
    return map
  }, [employeesData])

  const source = useTeamViewSource(
    company.id,
    hasJobCatalogTreeFeature,
    i18nDateToString(date, 'backend'),
    canSeeJobTitle,
    employeeFilters,
    employeeNameFilter
  )

  return (
    <ResponsivePage.Body
      authorized={canSeeCalendar}
      showHeader={false}
      loading={loading}
      error={null}
    >
      <Box style={{ position: 'relative' }}>
        <Box
          style={{
            position: 'absolute',
            top: '2px',
            right: '-8px',
          }}
          flexDirection="row"
          gap="s16"
          alignItems="center"
        >
          <DateNavigator
            regularPeriods={['month']}
            startDate={range[0]}
            endDate={range[1]}
            onDateRangeChange={(...newRange) => onDateRangeChange(newRange)}
          />
          <Exports />
        </Box>
        <Box paddingTop="s64">
          <OneDataCollection
            id="calendar/team-view/v1"
            source={source}
            visualizations={[
              {
                label: 'Calendar',
                icon: Calendar,
                type: 'custom',
                component: () => (
                  <TeamViewVisualization
                    source={source}
                    date={date}
                    changingData={changingData}
                    employeeLookup={employeeLookup}
                  >
                    {children}
                  </TeamViewVisualization>
                ),
              },
            ]}
          />
        </Box>
      </Box>
    </ResponsivePage.Body>
  )
}

export default observer(TeamViewPage)
